import { ApiError } from '@/lib/api';
import {
  MEMO_CONFLICT,
  NOTE_AUTHOR_ONLY,
  NOTE_REPLY_TO_REPLY,
  NOTE_ROOT_ONLY,
  NOTE_VISIBILITY_FORBIDDEN,
  type NoteTargetType,
  type NoteVisibility,
} from '@mss_creator/shared';

/**
 * Wording shared by the notes kit (issue #19, `docs/specs/matrices.md` « Notes
 * de matrice »): levels, dates, plurals and the sentence an API error turns
 * into. Pure, so the components stay thin and these are tested on their own.
 */

// ── Levels ───────────────────────────────────────────────────────────────────

export const NOTE_LEVEL_LABELS: Record<NoteVisibility, string> = {
  client: 'Client',
  pms: 'Équipe PMS',
  private: 'Privée',
};

/** What a level means, as the level picker words it. */
export function noteLevelDescription(visibility: NoteVisibility, companyName: string): string {
  switch (visibility) {
    case 'client':
      return `L'équipe PMS et les utilisateurs de ${companyName}`;
    case 'pms':
      return 'Les consultants PMS uniquement';
    case 'private':
      return 'Vous uniquement';
  }
}

/** The sentence under the composer, so nobody publishes to the client by mistake. */
export function visibleByPhrase(visibility: NoteVisibility, companyName: string): string {
  switch (visibility) {
    case 'client':
      return `Visible par ${companyName}`;
    case 'pms':
      return "Visible par l'équipe PMS";
    case 'private':
      return 'Visible par vous uniquement';
  }
}

// ── Dates ────────────────────────────────────────────────────────────────────

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Short date of a note: « à l'instant », « il y a 5 min », « il y a 3 h »,
 * « hier », then « 28 sept. » (with the year once it is not the current one).
 */
export function formatNoteDate(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const elapsed = now.getTime() - date.getTime();
  const sameDay = startOfDay(date) === startOfDay(now);
  if (elapsed >= 0 && elapsed < MINUTE_MS) return "à l'instant";
  if (elapsed >= 0 && elapsed < HOUR_MS) return `il y a ${Math.floor(elapsed / MINUTE_MS)} min`;
  if (elapsed >= 0 && sameDay) return `il y a ${Math.floor(elapsed / HOUR_MS)} h`;
  const dayMs = 24 * HOUR_MS;
  if (Math.round((startOfDay(now) - startOfDay(date)) / dayMs) === 1) return 'hier';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** Full date and time, for the `title` of a short date. */
export function formatNoteDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Wording ──────────────────────────────────────────────────────────────────

/** « 1 réponse », « 3 réponses ». */
export function replyCountLabel(count: number): string {
  return count === 1 ? '1 réponse' : `${count} réponses`;
}

/** « 1 message », « 3 messages ». */
export function messageCountLabel(count: number): string {
  return count === 1 ? '1 message' : `${count} messages`;
}

/** The author's name, or a stand-in once the account is gone. */
export function authorName(author: { displayName: string } | null): string {
  return author?.displayName ?? 'Utilisateur supprimé';
}

/** « Aucune note sur cette case. » adapted to the type of the target. */
export function emptyTargetMessage(type: NoteTargetType): string {
  switch (type) {
    case 'cell':
      return 'Aucune note sur cette case.';
    case 'row':
      return 'Aucune note sur cette ligne.';
    case 'column':
      return 'Aucune note sur cette colonne.';
    case 'folder':
      return 'Aucune note sur ce dossier.';
    case 'matrix':
      return 'Aucune note sur la matrice.';
  }
}

/** The sentence an error from a notes call turns into. */
export function noteErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case NOTE_VISIBILITY_FORBIDDEN:
        return "Ce niveau est réservé à l'équipe PMS. Choisissez Client ou Privée.";
      case NOTE_AUTHOR_ONLY:
        return "Seul l'auteur peut modifier ou supprimer cette note.";
      case NOTE_REPLY_TO_REPLY:
        return 'On ne peut pas répondre à une réponse.';
      case NOTE_ROOT_ONLY:
        return 'Cette action ne concerne que la première note de la discussion.';
      case MEMO_CONFLICT:
        return 'Le mémo a été modifié entre-temps. Rechargez pour voir sa version.';
    }
  }
  return fallback;
}
